import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import OceanWaves from './OceanWaves';
import styles from '../styles/Hero.module.scss';

const Hero: React.FC = () => {
  const navigate = useNavigate();

  const scrollToFeatures = () => {
    const featuresSection = document.getElementById('features');
    if (featuresSection) {
      featuresSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleReportHazard = () => {
    navigate('/reports');
  };

  return (
    <section className={styles.hero} id="home">
      {/* Three.js Ocean Background */}
      <OceanWaves />
      
      {/* Hero Content */}
      <div className={styles.heroContent}>
        <motion.div
          className={styles.heroCard}
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
        >
          <motion.h1
            className={styles.heroTitle}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            Real-Time Ocean Hazard
            <span className={styles.titleHighlight}> Reporting & Awareness </span>
            Platform
          </motion.h1>
          
          <motion.p
            className={styles.heroSubtext}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            Empowering citizens and authorities with data-driven insights for safer coasts.
            Join our community in protecting marine environments through collaborative monitoring.
          </motion.p>
          
          <motion.div
            className={styles.heroActions}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
          >
            <motion.button
              className={styles.ctaButton}
              whileHover={{ 
                scale: 1.05,
                boxShadow: '0 20px 40px rgba(0, 153, 255, 0.4)'
              }}
              whileTap={{ scale: 0.95 }}
              onClick={handleReportHazard}
            >
              <span className={styles.buttonText}>Report Hazard</span>
              <div className={styles.buttonGlow}></div>
            </motion.button>
            
            <motion.button
              className={styles.secondaryButton}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={scrollToFeatures}
            >
              Learn More
            </motion.button>
          </motion.div>
        </motion.div>
      </div>
      
      {/* Scroll Indicator */}
      <motion.div
        className={styles.scrollIndicator}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.2 }}
        onClick={scrollToFeatures}
      >
        <motion.div
          className={styles.scrollArrow}
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M7 10L12 15L17 10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </motion.div>
        <span className={styles.scrollText}>Scroll to explore</span>
      </motion.div>
      
      {/* Floating particles for ambiance */}
      <div className={styles.particles}>
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className={styles.particle}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ 
              opacity: [0, 1, 0],
              scale: [0, 1, 0],
              y: [-20, -100],
              x: [0, Math.random() * 100 - 50]
            }}
            transition={{
              duration: 4 + Math.random() * 2,
              repeat: Infinity,
              delay: i * 0.5,
              ease: 'easeOut'
            }}
          />
        ))}
      </div>
    </section>
  );
};

export default Hero;
